// =============================================================================
// Public Webinar Live Page — Chat-only viewer (no camera/mic)
// =============================================================================
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { webinarSessionAPI } from '@/lib/api';
import { io } from 'socket.io-client';
import { Send, AlertCircle, Radio, Users, Clock, MessageSquare, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicWebinarLivePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [participants, setParticipants] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const socketRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const { data } = await webinarSessionAPI.validateToken(token);
      setSessionData(data);
      if (data.webinar) {
        connectToLive(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou inscription non approuvée');
    } finally {
      setLoading(false);
    }
  };

  const connectToLive = (data) => {
    const socket = io(BACKEND_URL, {
      path: '/ws/socket.io',
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('webinar:join', {
        webinar_id: data.webinar.id,
        user_id: `guest_${data.registration.id}`,
        user_name: data.participant.name,
        user_role: 'viewer',
      });
    });

    socket.on('webinar:participants', (d) => setParticipants(d.participants || []));
    socket.on('webinar:state', (d) => {
      if (d.participants) setParticipants(d.participants);
    });
    socket.on('webinar:chat', (msg) => {
      setMessages(prev => [...prev.slice(-200), msg]);
    });

    return () => {
      socket.emit('webinar:leave');
      socket.disconnect();
    };
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Timer
  useEffect(() => {
    if (!sessionData?.webinar) return;
    const interval = setInterval(() => {
      const start = sessionData.webinar.schedule?.start_at;
      if (start) {
        const diff = Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 1000));
        setElapsed(diff);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionData]);

  const sendMessage = () => {
    if (!messageInput.trim() || !socketRef.current) return;
    socketRef.current.emit('webinar:chat', {
      content: messageInput.trim(),
      type: 'chat',
    });
    setMessageInput('');
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400 text-sm">Validation de votre accès...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Accès refusé</h1>
          <p className="text-zinc-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const webinar = sessionData?.webinar;
  const session = sessionData?.session;
  const participant = sessionData?.participant;

  // If no webinar is linked yet or not started
  if (!webinar) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="text-center max-w-md bg-zinc-900/60 rounded-2xl border border-zinc-800 p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Clock className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Webinaire à venir</h1>
          <h2 className="text-lg text-cyan-400 font-semibold mb-3">{session?.title}</h2>
          <p className="text-zinc-400 text-sm mb-4">
            Le webinaire n'a pas encore démarré. Revenez à l'heure prévue.
          </p>
          {session?.schedule?.start_at && (
            <div className="bg-zinc-800/60 rounded-xl p-4 border border-zinc-700">
              <p className="text-sm text-zinc-300">
                📅 {new Date(session.schedule.start_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-6">Bienvenue {participant?.name} 👋</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0a0e1a] flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="h-12 bg-zinc-900/90 backdrop-blur border-b border-zinc-800 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 text-red-400 text-xs font-semibold border border-red-500/20 animate-pulse">
            <Radio className="w-3 h-3" /> LIVE
          </span>
          <h1 className="text-sm font-semibold text-white truncate max-w-md">{session?.title || webinar?.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-zinc-500">
            <Clock className="w-3.5 h-3.5" /> {formatTime(elapsed)}
          </span>
          <span className="flex items-center gap-1 text-xs text-zinc-500">
            <Users className="w-3.5 h-3.5" /> {participants.length}
          </span>
          <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded-full border border-cyan-500/20">
            {participant?.name}
          </span>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex min-h-0">
        {/* Video / main area (viewer sees host video) */}
        <div className="flex-1 flex items-center justify-center bg-zinc-950 relative">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-600/20 border border-zinc-700 flex items-center justify-center mb-4">
              <Radio className="w-8 h-8 text-cyan-400" />
            </div>
            <p className="text-zinc-400 text-sm">Vous regardez le webinaire en mode spectateur</p>
            <p className="text-zinc-600 text-xs mt-1">Utilisez le chat pour communiquer</p>
          </div>

          {/* Info banner */}
          <div className="absolute bottom-4 left-4 right-4 bg-zinc-900/80 backdrop-blur rounded-xl border border-zinc-800 px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-zinc-500">
              👁️ Mode spectateur — Caméra et micro désactivés
            </span>
          </div>
        </div>

        {/* Chat panel */}
        <div className="w-80 border-l border-zinc-800 bg-zinc-900/50 flex flex-col shrink-0">
          <div className="px-4 py-3 border-b border-zinc-800">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-cyan-400" /> Chat en direct
            </h3>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-thin">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-xs text-zinc-600">Pas encore de messages</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={msg.id || i} className="group">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-600 to-violet-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0 mt-0.5">
                    {msg.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <span className="text-[10px] font-semibold text-cyan-400">{msg.user_name}</span>
                    <p className="text-xs text-zinc-300 break-words">{msg.content}</p>
                  </div>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Message input */}
          <div className="p-3 border-t border-zinc-800">
            <div className="flex gap-2">
              <input
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Écrire un message..."
                className="flex-1 h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-xs placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-cyan-500/40"
              />
              <button
                onClick={sendMessage}
                className="h-9 w-9 rounded-lg bg-gradient-to-r from-cyan-500 to-violet-600 flex items-center justify-center text-white hover:from-cyan-600 hover:to-violet-700 transition-all"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
